'use client'

import { usePathname, useRouter } from 'next/navigation'
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import type { PanelImperativeHandle } from 'react-resizable-panels'
import {
  CHAT_SIZE_PCT_KEY,
  readStoredString,
  writeStoredString,
} from '@/components/canvas/use-canvas-chats-utils'
import { confirmLeaveIfUnsaved } from '@/components/navigation/unsaved-changes-registry'
import {
  type ChatPanelView,
  readChatViewCookie,
  readChatViewParamFromLocation,
  withChatListView,
  writeChatViewCookie,
} from './chat-panel-view-state'
import {
  type ChatSide,
  oppositeChatSide,
  readChatSideCookie,
  writeChatSideCookie,
} from './chat-side'
import {
  FULL_CHAT_LIST_PATH,
  fullChatPath,
  isExcludedChatPath,
  isFullChatListPath,
  parseFullChatId,
} from './excluded-paths'

/** Default chat panel width (% of the row) in split mode. */
const DEFAULT_SPLIT_SIZE_PCT = 25
/** Clamp bounds for the persisted split width (mirrors the shell panel). */
const MIN_SPLIT_SIZE_PCT = 15
const MAX_SPLIT_SIZE_PCT = 80

/**
 * Read the persisted split width from localStorage, clamped to the valid
 * panel range. Falls back to {@link DEFAULT_SPLIT_SIZE_PCT} when unset or
 * malformed so a corrupted value can never strand the panel off-screen.
 */
function readStoredChatSizePct(): number {
  const raw = Number(
    readStoredString(CHAT_SIZE_PCT_KEY, String(DEFAULT_SPLIT_SIZE_PCT))
  )
  if (!Number.isFinite(raw)) {
    return DEFAULT_SPLIT_SIZE_PCT
  }
  return Math.min(Math.max(raw, MIN_SPLIT_SIZE_PCT), MAX_SPLIT_SIZE_PCT)
}

type UseChatSurfaceOptions = {
  /** Active chat of the shared tab strip (`useCanvasChats`). */
  activeChatId: string | null
  adoptActiveChat: (chatId: string) => void
  isLeftPanelOpen: boolean
  setIsLeftPanelOpen: (open: boolean) => void
  /**
   * True while a user-initiated brand switch is in flight (`setBrandId` in
   * brand-context.tsx). The switch flips `brandId` to the new brand a beat
   * before the /home navigation lands, so the URL can still point at the
   * previous brand's `/chat/<id>` while the active brand is already the
   * new one; full-page chat adoption is suppressed in that window so the
   * previous brand's chat is never written into the new brand's tab strip.
   */
  isBrandSwitching: boolean
  initialChatSide?: ChatSide
  initialChatView?: ChatPanelView
}

/** The presentation slice of `GlobalChatContextValue` (see its docs). */
export type ChatSurfaceState = {
  fullChatId: string | null
  isFullPage: boolean
  isCollapsed: boolean
  setIsCollapsed: (collapsed: boolean) => void
  panelRef: RefObject<PanelImperativeHandle | null>
  toggleCollapsed: () => void
  ensurePanelExpanded: () => void
  chatSizePct: number
  setChatSizePct: (pct: number) => void
  chatSide: ChatSide
  toggleChatSide: () => void
  chatPanelView: ChatPanelView
  setChatPanelView: (view: ChatPanelView) => void
  expandToFull: () => void
  openChatFullPage: (chatId: string) => void
  collapseFromFull: () => void
  openPathInSplit: (path: string) => void
}

/**
 * Where and how the chat column is shown: full-page (`/chat/<id>`,
 * `/chats`) vs the split side panel, the panel's persisted view (thread |
 * list), side, width and collapse state, and the navigations between
 * those presentations. Owned by `GlobalChatProvider`; kept apart from the
 * conversation state (tabs, attachments, drafts) it sits beside.
 */
export function useChatSurface({
  activeChatId,
  adoptActiveChat,
  isLeftPanelOpen,
  setIsLeftPanelOpen,
  isBrandSwitching,
  initialChatSide,
  initialChatView,
}: UseChatSurfaceOptions): ChatSurfaceState {
  const pathname = usePathname()
  const router = useRouter()

  // Full-page chat mode: `/chat/<id>` renders the thread at 100% width and
  // `/chats` renders the chat-list overview at 100% width.
  const fullChatId = parseFullChatId(pathname)
  const isFullList = isFullChatListPath(pathname)
  const isFullPage = fullChatId !== null || isFullList

  // True only in SPLIT mode — the one presentation where the resizable
  // panel both renders content and belongs at the user's saved width.
  // `/home` parks it at 0% with no content; `/chat/<id>` and `/chats`
  // drive it to 100%. Imperative panel sizing must respect that.
  const isPanelRouteVisible = !isFullPage && !isExcludedChatPath(pathname ?? '')

  // Persisted split-panel view (thread | list). Seeded from the SERVER-read
  // cookie so the first paint and hydration already agree on which header
  // and body the panel renders; the client cookie read is the fallback for
  // mounts without a server seed (tests, any future non-shell mount).
  const [chatPanelView, setChatPanelViewRaw] = useState<ChatPanelView>(
    () => initialChatView ?? readChatViewCookie()
  )
  const setChatPanelView = useCallback((view: ChatPanelView) => {
    writeChatViewCookie(view)
    setChatPanelViewRaw(view)
  }, [])

  // Adopt the path-encoded chat as active (no `?chat=` mirror) so the SAME
  // conversation persists when the user later navigates to a page (split mode
  // reads `activeChatId`). The full surfaces adopt the panel VIEW the same
  // way: leaving `/chats` for a page keeps the list in the side panel,
  // leaving `/chat/<id>` keeps that thread.
  //
  // The chat adopt is skipped while a brand switch is in flight (see
  // `isBrandSwitching`). The durable cross-brand-bleed fix is the
  // brand-ownership prune in `useCanvasChats` (it drops any tab whose chat
  // belongs to another brand, so a stale adopt self-heals); this guard just
  // avoids the transient write.
  useEffect(() => {
    if (isFullList) {
      setChatPanelView('list')
    } else if (fullChatId) {
      setChatPanelView('thread')
    }
    if (isBrandSwitching) {
      return
    }
    if (fullChatId && fullChatId !== activeChatId) {
      adoptActiveChat(fullChatId)
    }
  }, [
    fullChatId,
    isFullList,
    activeChatId,
    adoptActiveChat,
    isBrandSwitching,
    setChatPanelView,
  ])

  // Remember the last non-chat, non-excluded page so "collapse from full"
  // has somewhere sensible to land. Both full surfaces (`/chat/<id>` and
  // `/chats`) are skipped: collapsing to the side must never land on a page
  // whose only content IS the chat column.
  const lastPageRef = useRef<string>('/emails')
  useEffect(() => {
    if (!pathname) {
      return
    }
    if (parseFullChatId(pathname) || isFullChatListPath(pathname)) {
      return
    }
    if (isExcludedChatPath(pathname)) {
      return
    }
    lastPageRef.current = pathname
  }, [pathname])

  // Resizable-panel UI slice. The panel ref + collapse state live here
  // so the global header toggle (collapsed → chat icon) and the shell's
  // resizable panel stay in sync. Initial collapse follows the
  // persisted `isLeftPanelOpen` preference; an effect in the shell
  // calls `panelRef.collapse()` on mount when the panel should start
  // closed.
  const panelRef = useRef<PanelImperativeHandle>(null)
  const [isCollapsed, setIsCollapsed] = useState(!isLeftPanelOpen)

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((collapsed) => {
      const shouldCollapse = !collapsed
      if (shouldCollapse) {
        panelRef.current?.collapse()
        setIsLeftPanelOpen(false)
      } else {
        panelRef.current?.expand()
        setIsLeftPanelOpen(true)
      }
      return shouldCollapse
    })
  }, [setIsLeftPanelOpen])

  // Seed from localStorage (lazy) so the panel mounts at the user's last
  // resized width — no jump from a hard-coded default on reload / remount.
  const [chatSizePct, setChatSizePctRaw] = useState(readStoredChatSizePct)
  const chatSizePctRef = useRef(chatSizePct)

  const setChatSizePct = useCallback((pct: number) => {
    const clamped = Math.min(
      Math.max(pct, MIN_SPLIT_SIZE_PCT),
      MAX_SPLIT_SIZE_PCT
    )
    chatSizePctRef.current = clamped
    setChatSizePctRaw(clamped)
    writeStoredString(CHAT_SIZE_PCT_KEY, String(clamped))
  }, [])

  // Side preference. Seeded from the SERVER-read cookie (`initialChatSide`)
  // so the first paint is already on the user's edge and hydration matches;
  // the client cookie read is only the fallback for mounts without a server
  // seed (tests, any future non-shell mount).
  const [chatSide, setChatSide] = useState<ChatSide>(
    () => initialChatSide ?? readChatSideCookie()
  )

  const toggleChatSide = useCallback(() => {
    setChatSide((current) => {
      const next = oppositeChatSide(current)
      writeChatSideCookie(next)
      return next
    })
  }, [])

  const openChatFullPage = useCallback(
    (chatId: string) => {
      router.push(fullChatPath(chatId))
    },
    [router]
  )

  const expandToFull = useCallback(() => {
    // Dragging the side panel to full width keeps what it was showing: the
    // list expands to `/chats`, a thread to `/chat/<id>`. The URL override
    // is read at call time (a resize gesture) so the provider never
    // subscribes to search params.
    if (
      chatPanelView === 'list' ||
      readChatViewParamFromLocation() === 'list'
    ) {
      router.push(FULL_CHAT_LIST_PATH)
      return
    }
    const target = activeChatId ?? fullChatId
    if (!target) {
      return
    }
    openChatFullPage(target)
  }, [chatPanelView, activeChatId, fullChatId, openChatFullPage, router])

  const collapseFromFull = useCallback(() => {
    const lastPage = lastPageRef.current || '/emails'
    // Collapsing the full-width LIST keeps the user on the list: the side
    // panel opens on the overview (already adopted into the persisted view;
    // the param mirrors it onto the URL) instead of snapping back to a thread.
    router.push(isFullList ? withChatListView(lastPage) : lastPage)
  }, [router, isFullList])

  const ensurePanelExpanded = useCallback(() => {
    // Expand WITHOUT resetting the user's resized width — only force a
    // width when the panel is currently collapsed (there is no ratio to
    // preserve), restoring the last persisted width. When the panel group
    // isn't mounted (full-page chat / excluded routes) the resize is a
    // no-op and the split panel mounts at `defaultSize=chatSizePct`.
    //
    // The collapse test reads the PANEL, not `isCollapsed`: that state
    // tracks split-mode resizes only (the shell parks the panel to 0% on
    // excluded routes and to 100% on `/chat/<id>` without routing either
    // through `setIsCollapsed`), so it can report "open" for a panel that
    // is really shut. Gating on the stale value skipped the resize AND
    // made the `setIsCollapsed(false)` below a no-op — no re-render, so
    // the shell's layout effect never re-ran either and the panel stayed
    // closed. That was the templates page opening a chat nobody could see.
    //
    // …but ONLY on a route that actually shows the panel. On `/home` the
    // shell parks it at 0% and renders no panel content, so an imperative
    // expand there opens an EMPTY gap that survives until the next
    // navigation flips `mode` (the shell's layout effect keys off `mode` /
    // `isCollapsed`, and `setIsCollapsed(false)` is a no-op when it is
    // already false, so nothing re-renders to undo it). `openPathInSplit`
    // expands-then-navigates from exactly there. Recording the preference
    // is enough: arriving on a split route re-runs that layout effect.
    const panel = isPanelRouteVisible ? panelRef.current : null
    if (panel?.isCollapsed()) {
      panel.resize(`${chatSizePctRef.current}%`)
      // `resize` is clamped against the panel constraints; if the group
      // hasn't measured yet it can land back on `collapsedSize`.
      // `expand()` is the library's own restore path and is idempotent
      // once the panel is open.
      if (panel.isCollapsed()) {
        panel.expand()
      }
    }
    setIsCollapsed(false)
    setIsLeftPanelOpen(true)
  }, [isPanelRouteVisible, setIsLeftPanelOpen])

  const openPathInSplit = useCallback(
    (path: string) => {
      // A chat navigation pill has no link for the unsaved-changes guard to
      // intercept, so it asks the guard first (a dirty editor prompts). Then
      // open the chat beside the page, preserving the user's resized ratio
      // (persisted in `chatSizePct`) across the navigation so there is no
      // layout shift.
      void confirmLeaveIfUnsaved().then((shouldLeave) => {
        if (!shouldLeave) {
          return
        }
        ensurePanelExpanded()
        router.push(path)
      })
    },
    [router, ensurePanelExpanded]
  )

  // Stable identity: the provider spreads this into its memoized context
  // value, so a fresh object per render would re-render every consumer.
  return useMemo<ChatSurfaceState>(
    () => ({
      fullChatId,
      isFullPage,
      isCollapsed,
      setIsCollapsed,
      panelRef,
      toggleCollapsed,
      ensurePanelExpanded,
      chatSizePct,
      setChatSizePct,
      chatSide,
      toggleChatSide,
      chatPanelView,
      setChatPanelView,
      expandToFull,
      openChatFullPage,
      collapseFromFull,
      openPathInSplit,
    }),
    [
      fullChatId,
      isFullPage,
      isCollapsed,
      toggleCollapsed,
      ensurePanelExpanded,
      chatSizePct,
      setChatSizePct,
      chatSide,
      toggleChatSide,
      chatPanelView,
      setChatPanelView,
      expandToFull,
      openChatFullPage,
      collapseFromFull,
      openPathInSplit,
    ]
  )
}
